import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";

export type PaginationStateContextValue<T> = {
  currentPage: number;
  totalPages: number;
  currentPageItems: T[];
};

type PaginationActionsContextValue = {
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  goToPage: (page: number) => void;
};

export const PaginationStateContext = createContext<
  PaginationStateContextValue<unknown> | undefined
>(undefined);

export const PaginationActionsContext = createContext<
  PaginationActionsContextValue | undefined
>(undefined);

type PaginationContextProviderProps<T> = {
  children: ReactNode;
  maxItemsPerPage: number;
  items: T[];
  /** When this changes, pagination returns to page 1 — without remounting. */
  resetKey?: string;
};

export const PaginationContextProvider = <T,>({
  children,
  items = [],
  maxItemsPerPage,
  resetKey
}: PaginationContextProviderProps<T>) => {
  // state that will be updated by page navigation
  const [page, setPage] = useState(1);

  // Return to page 1 when resetKey changes, during render (no remount).
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (resetKey !== prevResetKey) {
    setPrevResetKey(resetKey);
    setPage(1);
  }

  const itemsCount = items?.length ?? 0;

  // Ensure itemsPerPage is at least 1 to avoid division by zero
  const itemsPerPage = Math.max(1, maxItemsPerPage);

  const totalPages = Math.max(0, Math.ceil(itemsCount / itemsPerPage));

  // actual current page derived from the state - will be updated at render in case of invalid page (e.g greater than total pages)
  const currentPage = page > totalPages ? totalPages : page;

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const currentPageItems = useMemo(
    () => items?.slice(startIndex, endIndex) ?? [],
    [items, startIndex, endIndex]
  );

  const goToNextPage = useCallback(() => {
    if (currentPage >= totalPages) return; // Prevent going beyond last page

    const nextPage = currentPage + 1;
    setPage(nextPage);
  }, [currentPage, totalPages]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      const previousPage = currentPage - 1;
      setPage(previousPage);
    }
  }, [currentPage]);

  const goToPage = useCallback(
    (newPage: number) => {
      if (newPage < 1 || newPage > totalPages) return; // Prevent going to invalid pages
      setPage(newPage);
    },
    [totalPages]
  );

  const paginationContextValue = useMemo(
    () => ({ currentPage, totalPages, currentPageItems }),
    [currentPage, totalPages, currentPageItems]
  );

  const paginationActionsContextValue = useMemo(
    () => ({
      goToNextPage,
      goToPreviousPage,
      goToPage
    }),
    [goToNextPage, goToPreviousPage, goToPage]
  );

  return (
    <PaginationStateContext.Provider value={paginationContextValue}>
      <PaginationActionsContext.Provider value={paginationActionsContextValue}>
        {children}
      </PaginationActionsContext.Provider>
    </PaginationStateContext.Provider>
  );
};

export function usePaginationContext<T>() {
  const context = useContext(PaginationStateContext);
  if (context === undefined) {
    throw new Error(
      "usePaginationContext must be used within a PaginationContext.Provider"
    );
  }
  return context as PaginationStateContextValue<T>;
}

export function usePaginationDispatchContext() {
  const context = useContext(PaginationActionsContext);
  if (context === undefined) {
    throw new Error(
      "usePaginationDispatchContext must be used within a PaginationDispatchContext.Provider"
    );
  }
  return context;
}
